import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import floorPlanImage from '../../Assets/Images/UnitSelection/floorPlan3.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import ConfirmUnitModal from '../OnlineBooking/ConfirmUnitModal';
import axios from 'axios';
import { getTokenServerTime } from '../../Services/Tokens';
import EventStatusDialog from '../OnlineBooking/EventStatusDialog';

//  *  Helpers
const parseToMillis = (value) => {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  // numeric string?
  const num = Number(value);
  if (Number.isFinite(num)) return num;

  // ISO or date-like string?
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

// Unit color mapping function
const getUnitColor = (unitId, isAvailable, isBooked, isRefuge) => {
  // if (isRefuge) return 'rgba(171, 129, 255, 0.6)'; // purple for refuge

  // if (isAvailable) return 'rgba(0, 190, 70, 0.6)'; // available green
  // if (isBooked) return 'rgba(180, 180, 180, 0.5)'; // muted gray booked

  // Base color fallback for plan zones
  // if (unitId >= 1 && unitId <= 4) return 'rgba(203, 74, 74, 0.6)';
  if ((unitId >= 3 && unitId <= 8) || (unitId >= 27 && unitId <= 33))
    return '#FFD0E9';
  if ((unitId >= 1 && unitId <= 2) || (unitId >= 9 && unitId <= 10))
    return 'rgb(189, 231, 255, 0.5)';
  // if (unitId === 13) return 'rgba(255, 180, 83, 0.6)';
  // if (unitId >= 14 && unitId <= 15) return 'rgba(239, 180, 193, 0.6)';
  // if (unitId >= 16 && unitId <= 21) return 'rgba(142, 189, 228, 0.6)';
  if (unitId === 11) return '#A5FDD6';
  if (unitId >= 12 && unitId <= 26) return '#FDE2A4';
  return 'rgba(230, 230, 230, 0.6)';
};

const OnlineBookingUnitSelection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { floorData, paymentId, tokenNo } = location.state || {};
  // console.log('floorData', floorData);

  const [selectedUnit, setSelectedUnit] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [labelCenters, setLabelCenters] = useState({});
  const [units, setUnits] = useState(floorData?.units || []);
  const [serverTimeMs, setServerTimeMs] = useState(null);
  const [config, setConfig] = useState(null);
  const [eventStatusDialog, setEventStatusDialog] = useState({
    open: false,
    title: '',
    message: '',
  });

  // Prevent double navigation
  const hasNavigatedRef = useRef(false);

  /** Fetch server time */
  const fetchServerTime = useCallback(async () => {
    try {
      const res = await getTokenServerTime(); // provided by your app
      const ms = parseToMillis(res?.data?.currentTime);
      setServerTimeMs(ms);
    } catch (e) {
      console.error('Failed to fetch server time:', e);
      // Optional: fallback to client time
      // setServerTimeMs(Date.now());
    }
  }, []);

  /** Fetch online booking config */
  const fetchOnlineBookingConfig = useCallback(async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_PAYMENT_LINK_URL}customerOnlineBooking/getEventOnlineBookingConfigDetails`,
        {
          eventId: units[0].launchId.eventId.eventId,
          launchId: units[0].launchId.launchId,
        }
      );
      setConfig(res?.data?.data ?? null);
    } catch (e) {
      console.error('Failed to fetch booking config:', e);
    }
  }, [units]);

  /** Load both, in parallel */
  useEffect(() => {
    (async () => {
      await Promise.allSettled([fetchServerTime(), fetchOnlineBookingConfig()]);
    })();
  }, [fetchServerTime, fetchOnlineBookingConfig]);

  /** Evaluate guards when data is ready */
  useEffect(() => {
    if (serverTimeMs == null || !config) return;

    // 1) Check acceptance flag first
    if (config.isOnlineBookingAccept === false) {
      setEventStatusDialog({
        open: true,
        title: 'Online Booking Disabled',
        message:
          'Online booking is currently not being accepted for this event. You’ll be redirected to the Tokens page.',
      });
      return;
    }

    // 2) Time window checks
    const startMs = parseToMillis(config.startDate);
    // const startMs = 1761656086200;
    const endMs = parseToMillis(config.endDate);
    // const endMs = 1761456086200;

    if (startMs != null && serverTimeMs < startMs) {
      setEventStatusDialog({
        open: true,
        title: 'Event Yet to Start',
        message:
          'This event hasn’t started yet. Please check back later. You’ll be redirected to the Tokens page.',
      });
      return;
    }

    if (endMs != null && serverTimeMs > endMs) {
      setEventStatusDialog({
        open: true,
        title: 'Event Ended',
        message:
          'This event has ended. Thank you for your interest. You’ll be redirected to the Tokens page.',
      });
      return;
    }

    // If we reach here, booking UI can proceed normally.
  }, [serverTimeMs, config]);

  /** Close dialog -> navigate to Tokens */
  const handleDialogClose = () => {
    setEventStatusDialog((d) => ({ ...d, open: false }));
    if (!hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate('/Home/Tokens');
    }
  };

  if (!floorData) {
    navigate('/Home/UnitFloorMap'); // Redirect if no floor data
  }

  const floorNo = floorData?.floorNo.split(' ')[1] || '';

  const refugeUnits = [23, 24]; // only these are labeled "Refuge"
  const refugeFloors = [7, 12, 17, 22]; // floors where these exist

  // filter available and booked based on units.unitId.inventoryStatusId (""=not available, "abcde"=available)
  const availableUnits = units
    .filter(
      (u) =>
        u.unitId.inventoryStatusId ===
          (process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION'
            ? '91c9faab-5adc-4b2f-a36e-b19160049413'
            : '91c9faab-5adc-4b2f-a36e-b19160049413') &&
        u.unitId.inventoryStatusId.trim() !== ''
    )
    .map((u) => Number(u.unitId.unitNo))
    .sort((a, b) => a - b);
  const bookedUnits = units
    .filter(
      (u) =>
        u.unitId.inventoryStatusId !==
          (process.env.REACT_APP_ENVIRONMENT === 'PRODUCTION'
            ? '91c9faab-5adc-4b2f-a36e-b19160049413'
            : '91c9faab-5adc-4b2f-a36e-b19160049413') &&
        u.unitId.inventoryStatusId.trim() !== ''
    )
    .map((u) => Number(u.unitId.unitNo));
  // console.log('availableUnits', availableUnits);
  // console.log('bookedUnits', bookedUnits);
  // const bookedUnits = [2, 5, 7, 10, 18, 19];
  // const availableUnits = [
  //   1, 3, 4, 6, 8, 9, 11, 12, 13,14, 15, 16, 17, 20, 21, 22, 23, 24, 25, 26, 27,
  //   28, 29, 30, 31, 32, 33,
  // ];

  // Your exported multi-path "d" from Figma
  const RAW_D = `
                M1152 92.5V33H1210.5V154H1152V130H1125V92.5H1152Z
                M1152 251V310.5H1210.5V189.5H1152V213.5H1125V251H1152Z
                M1064 188V310.5H1149.5V254H1122.5V188H1064Z
                M1002 250.5V310H1060.5V189H1002V213H975V250.5H1002Z
                M912 188V310.5H997.5V254H970.5V188H912Z
                M850 250.5V310H908.5V189H850V213H823V250.5H850Z
                M760 188V310.5H845.5V254H818.5V188H760Z 7
                M698 250.5V310H756.5V189H698V213H671V250.5H698Z
                M668 313.5L642 340L610.5 309V188.5H668V254H694.5V295.5H668V313.5Z
                M612.5 367H493.5V299.5H570.5V273.5H608.5V312L639.5 342L612.5 367Z
                M611.5 513.5H546.5V453.5H520V415.5H546.5V371H611.5V513.5Z
                M457.5 391V513.5H543V457H516V391H457.5Z
                M395.5 453.5V513H454V392H395.5V416H368.5V453.5H395.5Z
                M305.5 391V513.5H391V457H364V391H305.5Z
                M243.5 453.5V513H302V392H243.5V416H216.5V453.5H243.5Z
                M153.5 391V513.5H239V457H212V391H153.5Z 16
                M92 453.5V513H150.5V392H92V416H65V453.5H92Z
                M0.899051 392.5C0.241975 405.888 0.681762 477.697 1.00001 513.5H88V458H62V392.5H0.899051Z
                M1 359.5V237.5H88V293.5H63V359.5H1Z
                M93 297V237.5H151.5V358.5H93V334.5H66V297H93Z
                M154.5 359.5V237H240V293.5H213V359.5H154.5Z
                M244.5 297V237.5H303V358.5H244.5V334.5H217.5V297H244.5Z
                M153 123V1H240V57H215V123H153Z
                M244 61V1.5H302.5V122.5H244V98.5H217V61H244Z
                M305.5 123.5V1H391V57.5H364V123.5H305.5Z
                M395.5 61V1.5H454V122.5H395.5V98.5H368.5V61H395.5Z
                M608.5 155.5V33H694V89.5H667V155.5H608.5Z
                M698.5 93V33.5H757V154.5H698.5V130.5H671.5V93H698.5Z
                M760 155.5V33H845.5V89.5H818.5V155.5H760Z
                M850 93V33.5H908.5V154.5H850V130.5H823V93H850Z
                M912 155.5V33H997.5V89.5H970.5V155.5H912Z
                M1002 93V33.5H1060.5V154.5H1002V130.5H975V93H1002Z
                M1064 155.5V33H1149.5V89.5H1122.5V155.5H1064Z   
                `;
  // M1.00001 391C0.963806 391.362 0.930196 391.865 0.899051 392.5

  // 1) Split multi-subpath into separate <path>s
  // This regex grabs each subpath starting with 'M' up to (but not including) the next 'M'
  const subpaths = useMemo(() => {
    const parts = RAW_D.match(/M[^M]+/g) || [];
    return parts.map((d, i) => ({
      id: i + 1,
      name: `Unit ${i + 1}`,
      d: d.trim(),
    }));
  }, [RAW_D]);

  // ✅ Dynamically calculate text center for each path
  useEffect(() => {
    const centers = {};
    const leftSideUnits = [
      3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 29, 31, 33,
    ];
    const rightSectionUnits = [
      1, 2, 3, 4, 5, 6, 7, 8, 11, 28, 29, 30, 31, 32, 33,
    ];

    subpaths.forEach((p) => {
      const el = document.getElementById(`path-${p.id}`);
      if (el) {
        const box = el.getBBox();

        const isLeftSide = leftSideUnits.includes(p.id);
        const isRightSection = rightSectionUnits.includes(p.id);

        let visualCenterX = box.x + box.width / 2;
        let visualCenterY = box.y + box.height / 2;

        // 🧭 HORIZONTAL ADJUSTMENT
        // Each section & side has a slightly different “visual” offset
        // (because bounding boxes aren’t true geometric centers for complex shapes)

        if (isRightSection) {
          if (isLeftSide) {
            visualCenterX -= box.height * 0.12;
          } else {
            visualCenterX += box.height * 0.08;
          }
        } else {
          if (isLeftSide) {
            visualCenterX = visualCenterX - box.height * 0.08;
          } else {
            visualCenterX = visualCenterX + box.height * 0.125;
          }
        }
        if (p.id === 10) {
          visualCenterY += box.height * 0.12;
        }

        centers[p.id] = { x: visualCenterX, y: visualCenterY };
      }
    });
    setLabelCenters(centers);
  }, [subpaths]);

  const handleUnitSelection = (unitNumber) => {
    // console.log('handleUnitSelection', unitNumber);
    let selectedUnit = units.find(
      (u) => Number(u.unitId.unitNo) === unitNumber
    );
    // console.log('selectedUnit', selectedUnit);
    setSelectedUnit(selectedUnit);
  };

  // Custom hook to detect mobile view based on window width
  function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
  }

  // Usage:
  const isMobile = useIsMobile();

  // Api to get unit details by floorNo
  // POST ${process.env.REACT_APP_PAYMENT_LINK_URL}customerOnlineBooking/getAllUnitsForFloorFromLaunchesForCustomerFastBooking
  // Req.body::{"floorNo":"Floor 7"}

  useEffect(() => {
    let intervalId;
    const fetchUnits = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_PAYMENT_LINK_URL}customerOnlineBooking/getAllUnitsForFloorFromLaunchesForCustomerFastBooking`,
          { floorNo: floorData?.floorNo }
        );
        // console.log('res', res.data.data);
        setUnits(res.data.data);
      } catch (err) {
        // Handle error
        console.error('Error fetching units:', err);
      }
    };
    if (floorData?.floorNo) {
      fetchUnits();
      intervalId = setInterval(fetchUnits, 60000); // fetch every 60 seconds
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [!!selectedUnit, floorData?.floorNo]);

  return (
    <Box sx={{ width: '100%', overflow: `${!isMobile && 'auto'}` }}>
      <Box
        onClick={() => navigate('/Home/Tokens')}
        sx={{
          display: 'inline-block',
          fontSize: { xs: 14, sm: 14 },
          padding: 3,
          fontWeight: 600,
          textTransform: 'uppercase',
          background: 'linear-gradient(90deg, #0072ff 0%, #00b4e6ff 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          cursor: 'pointer',
          position: 'relative',
          letterSpacing: 0.5,
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: -3,
            width: '100%',
            height: 2,
            background: 'linear-gradient(90deg, #0072ff, #00b4e6ff)',
            transform: 'scaleX(0)',
            transformOrigin: 'right',
            transition: 'transform 0.3s ease',
          },
          '&:hover::after': {
            transform: 'scaleX(1)',
            transformOrigin: 'left',
          },
        }}>
        ← Go Back To Token
      </Box>

      {/* ✅ Guaranteed animations injected directly into DOM */}
      <style>{`
               /* Pulse effect for AVAILABLE units */
               @keyframes borderPulse {
                 0% {
                   stroke: rgba(27, 77, 45, 0.8);
                   stroke-width: 1.5;
                   filter: drop-shadow(0 0 2px rgba(0,190,70,0.2));
                 }
                 50% {
                   stroke: rgba(1, 114, 43, 1);
                   stroke-width: 2.5;
                   filter: drop-shadow(0 0 8px rgba(0,190,70,0.45));
                 }
                 100% {
                   stroke: rgba(27, 77, 45, 0.8);
                   stroke-width: 1.5;
                   filter: drop-shadow(0 0 2px rgba(0,190,70,0.2));
                 }
               }

               /* Pulse for selected (blue outline) */
               @keyframes borderSelected {
                 0% {
                   stroke: rgba(21,112,239,0.8);
                   stroke-width: 2;
                   filter: drop-shadow(0 0 4px rgba(21,112,239,0.4));
                 }
                 50% {
                   stroke: rgba(21,112,239,1);
                   stroke-width: 4;
                   filter: drop-shadow(0 0 10px rgba(21,112,239,0.6));
                 }
                 100% {
                   stroke: rgba(21,112,239,0.8);
                   stroke-width: 2;
                   filter: drop-shadow(0 0 4px rgba(21,112,239,0.4));
                 }
               }
              @keyframes chipGlow {
                 0%, 100% {
                   opacity: 1;
                   filter: brightness(1);
                 }
                 50% {
                   opacity: 0.85;
                   filter: brightness(1.2);
                 }
               }
    `}</style>
      <Box
        sx={{
          width: isMobile ? '80vh' : '100%',
          height: isMobile ? '100vw' : 'auto',
          // height: '100%',
          mx: 'auto',
          overflow: `${isMobile && 'scroll'}`,
          // display: 'flex',
          // justifyContent: 'center',
          // alignItems: 'center',
          background: 'rgb(255 255 255 / 0%)',
          paddingTop: 6,
          transform: isMobile
            ? 'rotate(90deg) translateY(30%) translateX(20%)'
            : 'none',
          transformOrigin: 'center center',
          transition: 'transform 0.4s ease-in-out',
          // position: 'absolute',
        }}>
        {/* One SVG that contains BOTH the raster image and vector paths
          -> guarantees perfect alignment because they share the same viewBox */}
        <svg
          // width="1211"
          // height="513"
          // width="100%"
          // height="100%"
          width={isMobile ? '110vh' : '100%'}
          height={isMobile ? '110vw' : '100%'}
          viewBox="0 0 1263 557"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <image
            href={floorPlanImage}
            x="-27"
            y="-26"
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid meet"
          />
          {subpaths.map((p) => {
            const unitNumber = Number(
              `${floorNo}${String(p.id).padStart(2, '0')}`
            );
            // const unitType = units.find(
            //   (u) => Number(u.unitId.unitNo) === unitNumber
            // )?.unitId.inventoryTypeId.type;
            const carpetArea = units.find(
              (u) => Number(u.unitId.unitNo) === unitNumber
            )?.unitId.chargeableArea;

            const carpetAreaInSqFt = carpetArea
              ? Math.floor(Number(carpetArea) * 10.7639)
              : 0; // Convert to sqft

            // Total cost in Indian Rupees format and in lakhs
            const totalCost = units.find(
              (u) => Number(u.unitId.unitNo) === unitNumber
            )?.unitId.totalCost;
            const price = totalCost
              ? `₹ ${(totalCost / 100000).toFixed(2)} L`
              : 'N/A';

            const isHover = hoverId === p.id && !isMobile;
            const isSelected =
              Number(selectedUnit?.unitId.unitNo) === unitNumber;

            const center = labelCenters[p.id] || { x: 0, y: 0 };

            const isRefuge =
              refugeUnits.includes(p.id) &&
              refugeFloors.includes(Number(floorNo));
            const isAvailable = availableUnits.includes(unitNumber);
            const isBooked = bookedUnits.includes(unitNumber);

            // const baseColor = getUnitColor(
            //   p.id,
            //   isAvailable,
            //   isBooked,
            //   isRefuge
            // );

            const notAvailable = isBooked || isRefuge;

            const labelY = center ? center.y : 0;
            const chipY = labelY + 15; // chip below number

            const statusColor =
              isAvailable && !isRefuge
                ? 'rgb(77, 145, 2)'
                : isBooked
                ? 'rgb(181, 24, 1)'
                : isRefuge
                ? 'rgba(171,129,255,0.8)'
                : 'rgba(200,200,200,0.6)';

            return (
              <g key={p.id}>
                <path
                  key={p.id}
                  id={`path-${p.id}`}
                  d={p.d}
                  stroke="black"
                  strokeWidth={isSelected ? 3 : isHover ? 1 : 0}
                  fill={
                    // notAvailable
                    //   ? 'rgba(184, 184, 184, 1)' // booked fill (greyed out)
                    //   :
                    isSelected
                      ? 'rgba(21,112,239,0.45)' // selected fill (brand blue)
                      : isHover
                      ? 'rgba(138, 255, 134, 0.3)' // hover fill
                      : //  ? 'rgba(21,112,239,0.25)' // hover fill
                        // : 'rgba(138, 255, 134, 0.3)'
                        'rgba(0, 0, 0, 0.02)'
                  }
                  // fill={getUnitColor(p.id, isAvailable, isBooked, isRefuge)}
                  // Ensure pointer events hit even if fill is transparent / path not closed
                  style={{
                    cursor: notAvailable ? '' : 'pointer',
                    pointerEvents: notAvailable ? 'none' : 'all',
                    transition: 'all 0.25s ease-out',
                    transformBox: 'fill-box',
                    transformOrigin: 'center',
                    transform: isSelected
                      ? 'scale(1.05)' // slight lift when selected
                      : isHover
                      ? 'scale(1.1)' // gentle zoom on hover
                      : 'scale(1)',
                    filter: notAvailable
                      ? 'grayscale(0.5) brightness(0.9)'
                      : 'none',
                    opacity: notAvailable ? 0.75 : 1,
                    // animation:
                    //   isAvailable && !isRefuge
                    //     ? 'borderPulse 2s ease-in-out infinite'
                    //     : isSelected
                    //     ? 'borderSelected 2s ease-in-out infinite'
                    //     : isRefuge
                    //     ? 'softGlow 4s ease-in-out infinite'
                    //     : 'none',
                  }}
                  onMouseEnter={() => setHoverId(p.id)}
                  onMouseLeave={() => setHoverId(null)}
                  onClick={() => {
                    setSelectedUnit(() => handleUnitSelection(unitNumber));
                  }}
                />
                {/* Old UnitNo design */}
                {/* {center && (
                  <>
                    <text
                      x={center.x}
                      y={center.y - 14}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize={isHover ? '10' : '8'}
                      fontWeight={isSelected ? '700' : '600'}
                      fill={isRefuge ? '#6F42C1' : '#333'}
                      transition="all 0.25s ease-out"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }>
                      {!isRefuge ? unitType : ''}
                    </text>
                    <text
                      x={center.x}
                      y={center.y}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize={isHover ? '14' : '12'}
                      fontWeight={isSelected ? '700' : '600'}
                      fill={isRefuge ? '#6F42C1' : '#333'}
                      transition="all 0.25s ease-out"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }>
                      {isRefuge
                        ? 'Refuge'
                        : floorNo + String(p.id).padStart(2, '0')}
                    </text>

                    <rect
                      x={center.x - 23}
                      y={chipY - 7}
                      rx={4}
                      width={46}
                      height={16}
                      fill={statusColor}
                      style={{
                        animation:
                          ((isAvailable || isSelected) && !isRefuge)
                            ? 'chipGlow 0.5s ease-in-out infinite'
                            : 'none',
                        filter:
                          isAvailable || isSelected
                            ? 'drop-shadow(0 0 6px rgba(0,190,70,0.4))'
                            : 'none',
                      }}
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }
                    />
                    <text
                      x={center.x}
                      y={chipY + 4}
                      textAnchor="middle"
                      fill="white"
                      fontSize="9"
                      fontWeight="500"
                      color="black"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }>
                      {isRefuge
                        ? 'Refuge'
                        : isAvailable
                        ? 'Available'
                        : isBooked
                        ? 'Booked'
                        : ''}
                    </text>
                  </>
                )} */}

                {center && (
                  <>
                    {!isRefuge && (
                      <>
                        {/* === OUTER BORDER CONTAINER === */}
                        <rect
                          x={center.x - 20.5} // horizontal offset
                          y={center.y - 25} // vertical offset
                          width={41}
                          height={55}
                          rx={3}
                          fill={statusColor}
                          stroke="#000"
                          strokeWidth="1"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                          style={{
                            animation:
                              isAvailable && !isRefuge
                                ? 'chipGlow 0.5s ease-in-out infinite'
                                : 'none',
                          }}
                        />
                      </>
                    )}

                    {/* === MAIN UNIT NUMBER === */}
                    <text
                      x={center.x}
                      y={isRefuge ? center.y : center.y - 15}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize={isRefuge ? '10' : '12'}
                      fontWeight="900"
                      fill={isRefuge ? '#6F42C1' : '#fff'}
                      transition="all 0.25s ease-out"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }>
                      {isRefuge
                        ? 'Refuge'
                        : floorNo + String(p.id).padStart(2, '0')}
                    </text>

                    {!isRefuge && (
                      <>
                        {/* === DIVIDER LINE BELOW UNIT NUMBER === */}
                        <line
                          x1={center.x - 16}
                          y1={center.y - 8}
                          x2={center.x + 16}
                          y2={center.y - 8}
                          stroke="#fff"
                          strokeWidth="0.8"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                        />

                        {/* === AREA === */}
                        <text
                          x={center.x}
                          y={center.y - 2}
                          textAnchor="middle"
                          alignmentBaseline="middle"
                          fontSize="7"
                          fontWeight="900"
                          fill="#fff"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }>
                          {carpetAreaInSqFt} Sq Ft
                        </text>

                        {/* === PRICE CHIP (Green box) === */}
                        <rect
                          x={center.x - 20}
                          y={center.y + 4}
                          // rx={3}
                          width={40}
                          height={12}
                          // fill={
                          //   isAvailable
                          //     ? '#44B564'
                          //     : isBooked
                          //     ? '#D32F2F'
                          //     : '#555'
                          // }
                          fill="#fff"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }
                        />
                        <text
                          x={center.x}
                          y={center.y + 12}
                          textAnchor="middle"
                          fontSize="8"
                          fontWeight="600"
                          fill="black"
                          pointerEvents="none"
                          transform={
                            isMobile
                              ? `rotate(-90, ${center.x}, ${center.y})`
                              : undefined
                          }>
                          {!isBooked && !isRefuge ? price : 0}
                        </text>
                      </>
                    )}

                    {/* === AVAILABILITY STATUS === */}
                    <text
                      x={center.x}
                      y={center.y + 23}
                      textAnchor="middle"
                      alignmentBaseline="middle"
                      fontSize="6"
                      fontWeight="400"
                      // fill={
                      //   isAvailable ? '#44B564' : isBooked ? '#D32F2F' : '#555'
                      // }
                      fill="#fff"
                      pointerEvents="none"
                      transform={
                        isMobile
                          ? `rotate(-90, ${center.x}, ${center.y})`
                          : undefined
                      }
                      style={{
                        animation:
                          isAvailable && !isRefuge
                            ? 'chipGlow 1s ease-in-out infinite'
                            : 'none',
                      }}>
                      {isAvailable && !isRefuge
                        ? 'AVAILABLE'
                        : isBooked
                        ? 'SOLD'
                        : ''}
                    </text>
                  </>
                )}
              </g>
            );
          })}
          <text
            x={850}
            y={420}
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize={isMobile ? 16 : 20}
            fontWeight="700"
            fill="#000"
            pointerEvents="none"
            transform={isMobile ? `rotate(-90, ${850}, ${420})` : undefined}>
            {isMobile ? (
              <>
                <tspan x={850} dy="-0.4em">
                  YOU ARE AT
                </tspan>
                <tspan x={850} dy="1.2em">
                  FLOOR : {floorNo}
                </tspan>
              </>
            ) : (
              <>YOU ARE AT FLOOR : {floorNo}</>
            )}
          </text>
        </svg>
      </Box>
      {/* Centered floor label */}
      {/* <Box
        sx={{
          position: 'absolute',
          bottom: isMobile ? '15%' : '6%',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center',
          fontWeight: 700,
          fontSize: isMobile ? '16px' : '22px',
          color: '#000',
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
        }}>
        YOU ARE WATCHING FLOOR : {floorNo}
      </Box> */}

      {/* Fixed compass / north icon */}
      <Box
        sx={{
          position: 'fixed',
          top: isMobile ? '8vh' : '10vh',
          right: isMobile ? '8vw' : '4vw',
          width: 64,
          height: 64,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 30% 30%, #fff 0%, #f1f1f1 100%)',
          boxShadow: '0 4px 10px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          transform: isMobile ? 'rotate(90deg)' : 'none',
        }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 100 100"
          width="64"
          height="64">
          {/* Outer circle */}
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="white"
            stroke="#333"
            strokeWidth="3"
          />

          {/* North arrow (triangle) */}
          <polygon
            points="50,40 63,80 50,67 37,80"
            fill="#e53935" // red arrow
            stroke="#b71c1c"
            strokeWidth="2"
          />

          {/* 'N' label */}
          <text
            x="50"
            y="25"
            textAnchor="middle"
            fontSize="26"
            fontWeight="700"
            fill="#111"
            alignmentBaseline="middle">
            N
          </text>
        </svg>
      </Box>

      {selectedUnit && (
        <ConfirmUnitModal
          isOpen={!!selectedUnit} // Open modal if selectedUnit is set
          onClose={() => setSelectedUnit(null)} // Close the modal
          unitData={selectedUnit} // Pass selectedUnit data to the modal
          paymentId={paymentId}
          tokenNo={tokenNo}
        />
      )}
      <EventStatusDialog
        open={eventStatusDialog.open}
        title={eventStatusDialog.title}
        message={eventStatusDialog.message}
        actionLabel="Go to Tokens"
        onClose={handleDialogClose}
      />
    </Box>
  );
};

export default OnlineBookingUnitSelection;
